import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  FileCode, Upload, Download, Copy, Lock, Unlock, Image as ImageIcon, 
  FileText, Binary, ArrowRightLeft, Info, Sparkles, Eye, EyeOff
} from 'lucide-react';

// Simple XOR encryption
const xorEncrypt = (data, password) => {
  if (!password) return data;
  const encoder = new TextEncoder();
  const dataBytes = typeof data === 'string' ? encoder.encode(data) : new Uint8Array(data);
  const passwordBytes = encoder.encode(password);
  
  const encrypted = new Uint8Array(dataBytes.length);
  for (let i = 0; i < dataBytes.length; i++) {
    encrypted[i] = dataBytes[i] ^ passwordBytes[i % passwordBytes.length];
  }
  return encrypted;
};

const xorDecrypt = (encryptedData, password) => {
  return xorEncrypt(encryptedData, password); // XOR is symmetric
};

const FileEncoder = () => {
  // Text to Base64 state
  const [textInput, setTextInput] = useState('');
  const [base64Output, setBase64Output] = useState('');
  const [textPassword, setTextPassword] = useState('');
  const [encryptText, setEncryptText] = useState(false);
  
  // Base64 to Text state
  const [base64Input, setBase64Input] = useState('');
  const [textOutput, setTextOutput] = useState('');
  const [decodePassword, setDecodePassword] = useState('');
  const [isEncrypted, setIsEncrypted] = useState(false);
  
  // File to Base64 state
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileBase64, setFileBase64] = useState('');
  const [filePassword, setFilePassword] = useState('');
  const [encryptFile, setEncryptFile] = useState(false);
  
  // Text to Image state
  const [imageText, setImageText] = useState('');
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [imageBgColor, setImageBgColor] = useState('#1a1a2e');
  const [imageTextColor, setImageTextColor] = useState('#04D9FF');
  const [imageFontSize, setImageFontSize] = useState('16');
  
  // Image to Text state (hidden text in image)
  const [hideText, setHideText] = useState('');
  const [carrierImage, setCarrierImage] = useState(null);
  const [stegoImage, setStegImage] = useState('');
  const [extractedText, setExtractedText] = useState('');
  
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);
  const carrierInputRef = useRef(null);
  const extractInputRef = useRef(null);

  // Text to Base64
  const encodeTextToBase64 = () => {
    try {
      let dataToEncode = textInput;
      
      if (encryptText && textPassword) {
        const encrypted = xorEncrypt(textInput, textPassword);
        dataToEncode = 'ENCRYPTED:' + btoa(String.fromCharCode(...encrypted));
      } else {
        dataToEncode = btoa(unescape(encodeURIComponent(textInput)));
      }
      
      setBase64Output(dataToEncode);
      toast.success('Text encoded to Base64!');
    } catch (error) {
      toast.error('Failed to encode text');
      console.error(error);
    }
  };

  // Base64 to Text
  const decodeBase64ToText = () => {
    try {
      let result = '';
      
      if (base64Input.startsWith('ENCRYPTED:')) {
        setIsEncrypted(true);
        if (!decodePassword) {
          toast.error('This content is encrypted. Please enter the password.');
          return;
        }
        const encryptedB64 = base64Input.substring(10);
        const encryptedBytes = Uint8Array.from(atob(encryptedB64), c => c.charCodeAt(0));
        const decrypted = xorDecrypt(encryptedBytes, decodePassword);
        result = new TextDecoder().decode(decrypted);
      } else {
        result = decodeURIComponent(escape(atob(base64Input)));
      }
      
      setTextOutput(result);
      toast.success('Base64 decoded!');
    } catch (error) {
      toast.error('Failed to decode Base64. Check if the input is valid.');
      console.error(error);
    }
  };

  // File to Base64
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        let base64Data = event.target.result;
        
        if (encryptFile && filePassword) {
          // For encrypted files, we need to handle the binary data
          const base64Content = base64Data.split(',')[1];
          const binaryData = atob(base64Content);
          const bytes = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            bytes[i] = binaryData.charCodeAt(i);
          }
          const encrypted = xorEncrypt(bytes, filePassword);
          const encryptedB64 = btoa(String.fromCharCode(...encrypted));
          base64Data = `ENCRYPTED_FILE:${file.type}:${encryptedB64}`;
        }
        
        setFileBase64(base64Data);
        toast.success('File converted to Base64!');
      };
      reader.readAsDataURL(file);
    }
  };

  // Text to Image
  const convertTextToImage = () => {
    if (!imageText.trim()) {
      toast.error('Please enter some text');
      return;
    }
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const fontSize = parseInt(imageFontSize);
    const padding = 40;
    const lineHeight = fontSize * 1.5;
    
    ctx.font = `${fontSize}px monospace`;
    
    // Split text into lines and calculate dimensions
    const lines = imageText.split('\n');
    const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
    
    canvas.width = Math.max(400, maxLineWidth + padding * 2);
    canvas.height = Math.max(200, lines.length * lineHeight + padding * 2);
    
    // Draw background
    ctx.fillStyle = imageBgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw text
    ctx.fillStyle = imageTextColor;
    ctx.font = `${fontSize}px monospace`;
    ctx.textBaseline = 'top';
    
    lines.forEach((line, index) => {
      ctx.fillText(line, padding, padding + index * lineHeight);
    });
    
    setImageDataUrl(canvas.toDataURL('image/png'));
    toast.success('Text converted to image!');
  };

  // Simple steganography - hide text in image
  const hideTextInImage = () => {
    if (!carrierImage || !hideText) {
      toast.error('Please select an image and enter text to hide');
      return;
    }
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Encode text length and content into LSB of pixels
      const textBytes = new TextEncoder().encode(hideText);
      const totalBits = (textBytes.length + 4) * 8; // 4 bytes for length
      
      if (totalBits > data.length) {
        toast.error('Image too small to hide this text');
        return;
      }
      
      // Store length first (4 bytes)
      const lengthBytes = new Uint8Array(4);
      new DataView(lengthBytes.buffer).setUint32(0, textBytes.length);
      
      const allBytes = new Uint8Array(4 + textBytes.length);
      allBytes.set(lengthBytes);
      allBytes.set(textBytes, 4);
      
      let bitIndex = 0;
      for (let i = 0; i < allBytes.length && bitIndex < data.length * 8; i++) {
        for (let bit = 7; bit >= 0 && bitIndex < data.length; bit--) {
          const pixelIndex = Math.floor(bitIndex / 3) * 4 + (bitIndex % 3);
          if (pixelIndex < data.length) {
            const bitValue = (allBytes[i] >> bit) & 1;
            data[pixelIndex] = (data[pixelIndex] & 0xFE) | bitValue;
          }
          bitIndex++;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      setStegImage(canvas.toDataURL('image/png'));
      toast.success('Text hidden in image!');
    };
    img.src = carrierImage;
  };

  // Extract hidden text from image
  const handleExtractImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          // Extract length first
          const lengthBits = [];
          let bitIndex = 0;
          for (let i = 0; i < 32; i++) {
            const pixelIndex = Math.floor(bitIndex / 3) * 4 + (bitIndex % 3);
            lengthBits.push(data[pixelIndex] & 1);
            bitIndex++;
          }
          
          let length = 0;
          for (let i = 0; i < 32; i++) {
            length = (length << 1) | lengthBits[i];
          }
          
          if (length <= 0 || length > 10000) {
            toast.error('No hidden text found or invalid image');
            return;
          }
          
          // Extract text bytes
          const textBytes = new Uint8Array(length);
          for (let byteIdx = 0; byteIdx < length; byteIdx++) {
            let byte = 0;
            for (let bit = 7; bit >= 0; bit--) {
              const pixelIndex = Math.floor(bitIndex / 3) * 4 + (bitIndex % 3);
              byte = (byte << 1) | (data[pixelIndex] & 1);
              bitIndex++;
            }
            textBytes[byteIdx] = byte;
          }
          
          const extractedText = new TextDecoder().decode(textBytes);
          setExtractedText(extractedText);
          toast.success('Text extracted from image!');
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCarrierImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCarrierImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadFile = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    link.click();
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
          File Encoder
        </h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Convert files between formats with optional encryption. Text to image, file to Base64, and more.
        </p>
      </div>

      <Tabs defaultValue="text-base64" className="w-full">
        <TabsList className="grid w-full grid-cols-5 lg:w-[900px] mx-auto mb-8 bg-gray-800/50">
          <TabsTrigger value="text-base64" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-text-base64">
            <FileText className="w-4 h-4" />
            Text → Base64
          </TabsTrigger>
          <TabsTrigger value="base64-text" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-base64-text">
            <Binary className="w-4 h-4" />
            Base64 → Text
          </TabsTrigger>
          <TabsTrigger value="file-base64" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-file-base64">
            <FileCode className="w-4 h-4" />
            File → Base64
          </TabsTrigger>
          <TabsTrigger value="text-image" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-text-image">
            <ImageIcon className="w-4 h-4" />
            Text → Image
          </TabsTrigger>
          <TabsTrigger value="steganography" className="flex items-center gap-2 data-[state=active]:bg-cyan-600" data-testid="tab-steganography">
            <Eye className="w-4 h-4" />
            Steganography
          </TabsTrigger>
        </TabsList>

        {/* Text to Base64 */}
        <TabsContent value="text-base64" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Input Text
                </CardTitle>
                <CardDescription className="text-gray-400">Enter text to encode to Base64</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Enter your text here..."
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  className="min-h-[200px] bg-gray-900 border-gray-600 text-white"
                  data-testid="text-to-base64-input"
                />
                
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-700">
                  <div className="space-y-0.5">
                    <Label className="text-white flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Encrypt before encoding
                    </Label>
                    <p className="text-sm text-gray-400">Add XOR encryption</p>
                  </div>
                  <Switch checked={encryptText} onCheckedChange={setEncryptText} />
                </div>
                
                {encryptText && (
                  <div className="space-y-2">
                    <Label className="text-white">Encryption Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter password"
                        value={textPassword}
                        onChange={(e) => setTextPassword(e.target.value)}
                        className="bg-gray-900 border-gray-600 text-white pr-10"
                        data-testid="text-encrypt-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={encodeTextToBase64} 
                  className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
                  data-testid="encode-text-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Encode to Base64
                </Button>
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Binary className="w-5 h-5 text-purple-400" />
                  Base64 Output
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  readOnly
                  value={base64Output}
                  placeholder="Encoded Base64 will appear here..."
                  className="min-h-[200px] bg-gray-900 border-gray-600 text-white font-mono"
                  data-testid="base64-output"
                />
                
                <div className="flex gap-2">
                  <Button 
                    onClick={() => copyToClipboard(base64Output)} 
                    variant="outline" 
                    className="flex-1 border-gray-600 text-white hover:bg-gray-700"
                    disabled={!base64Output}
                    data-testid="copy-base64-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button 
                    onClick={() => downloadFile(`data:text/plain;base64,${btoa(base64Output)}`, 'encoded.txt')} 
                    variant="outline" 
                    className="flex-1 border-gray-600 text-white hover:bg-gray-700"
                    disabled={!base64Output}
                    data-testid="download-base64-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Base64 to Text */}
        <TabsContent value="base64-text" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Binary className="w-5 h-5 text-cyan-400" />
                  Base64 Input
                </CardTitle>
                <CardDescription className="text-gray-400">Paste Base64 encoded text to decode</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Paste Base64 encoded text here..."
                  value={base64Input}
                  onChange={(e) => {
                    setBase64Input(e.target.value);
                    setIsEncrypted(e.target.value.startsWith('ENCRYPTED:'));
                  }}
                  className="min-h-[200px] bg-gray-900 border-gray-600 text-white font-mono"
                  data-testid="base64-decode-input"
                />
                
                {isEncrypted && (
                  <Alert className="bg-amber-900/30 border-amber-600/50">
                    <Lock className="w-4 h-4 text-amber-400" />
                    <AlertDescription className="text-amber-200">
                      This content is encrypted. Enter password to decrypt.
                    </AlertDescription>
                  </Alert>
                )}
                
                {isEncrypted && (
                  <div className="space-y-2">
                    <Label className="text-white">Decryption Password</Label>
                    <Input
                      type="password"
                      placeholder="Enter password"
                      value={decodePassword}
                      onChange={(e) => setDecodePassword(e.target.value)}
                      className="bg-gray-900 border-gray-600 text-white"
                      data-testid="decode-password"
                    />
                  </div>
                )}
                
                <Button 
                  onClick={decodeBase64ToText} 
                  className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
                  data-testid="decode-base64-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Decode from Base64
                </Button>
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Decoded Text
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  readOnly
                  value={textOutput}
                  placeholder="Decoded text will appear here..."
                  className="min-h-[200px] bg-gray-900 border-gray-600 text-white"
                  data-testid="decoded-text-output"
                />
                
                <Button 
                  onClick={() => copyToClipboard(textOutput)} 
                  variant="outline" 
                  className="w-full border-gray-600 text-white hover:bg-gray-700"
                  disabled={!textOutput}
                  data-testid="copy-decoded-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Decoded Text
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* File to Base64 */}
        <TabsContent value="file-base64" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Upload className="w-5 h-5 text-cyan-400" />
                  Upload File
                </CardTitle>
                <CardDescription className="text-gray-400">Select any file to convert to Base64</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-cyan-400 transition-colors"
                >
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-white font-medium">Click to select a file</p>
                  <p className="text-gray-400 text-sm mt-1">or drag and drop</p>
                  {selectedFile && (
                    <p className="text-cyan-400 mt-4">{selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)</p>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                
                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-700">
                  <div className="space-y-0.5">
                    <Label className="text-white flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Encrypt file data
                    </Label>
                    <p className="text-sm text-gray-400">Add XOR encryption</p>
                  </div>
                  <Switch checked={encryptFile} onCheckedChange={setEncryptFile} />
                </div>
                
                {encryptFile && (
                  <div className="space-y-2">
                    <Label className="text-white">File Encryption Password</Label>
                    <Input
                      type="password"
                      placeholder="Enter password"
                      value={filePassword}
                      onChange={(e) => setFilePassword(e.target.value)}
                      className="bg-gray-900 border-gray-600 text-white"
                      data-testid="file-encrypt-password"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Binary className="w-5 h-5 text-purple-400" />
                  Base64 Output
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  readOnly
                  value={fileBase64}
                  placeholder="Base64 encoded file data will appear here..."
                  className="min-h-[200px] bg-gray-900 border-gray-600 text-white font-mono text-xs"
                  data-testid="file-base64-output"
                />
                
                <div className="flex gap-2">
                  <Button 
                    onClick={() => copyToClipboard(fileBase64)} 
                    variant="outline" 
                    className="flex-1 border-gray-600 text-white hover:bg-gray-700"
                    disabled={!fileBase64}
                    data-testid="copy-file-base64-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button 
                    onClick={() => downloadFile(`data:text/plain;base64,${btoa(fileBase64)}`, 'file-base64.txt')} 
                    variant="outline" 
                    className="flex-1 border-gray-600 text-white hover:bg-gray-700"
                    disabled={!fileBase64}
                    data-testid="download-file-base64-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download as Text
                  </Button>
                </div>
                
                {fileBase64 && (
                  <Alert className="bg-blue-900/30 border-blue-600/50">
                    <Info className="w-4 h-4 text-blue-400" />
                    <AlertDescription className="text-blue-200">
                      Output size: {(fileBase64.length / 1024).toFixed(2)} KB
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Text to Image */}
        <TabsContent value="text-image" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Text Input
                </CardTitle>
                <CardDescription className="text-gray-400">Enter text to convert to an image</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Enter text to convert to image..."
                  value={imageText}
                  onChange={(e) => setImageText(e.target.value)}
                  className="min-h-[150px] bg-gray-900 border-gray-600 text-white font-mono"
                  data-testid="text-to-image-input"
                />
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Background</Label>
                    <div className="flex gap-2">
                      <div 
                        className="w-10 h-10 rounded border-2 border-gray-600 cursor-pointer overflow-hidden"
                        style={{ backgroundColor: imageBgColor }}
                      >
                        <input
                          type="color"
                          value={imageBgColor}
                          onChange={(e) => setImageBgColor(e.target.value)}
                          className="w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                      <Input
                        value={imageBgColor}
                        onChange={(e) => setImageBgColor(e.target.value)}
                        className="flex-1 bg-gray-900 border-gray-600 text-white text-xs"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Text Color</Label>
                    <div className="flex gap-2">
                      <div 
                        className="w-10 h-10 rounded border-2 border-gray-600 cursor-pointer overflow-hidden"
                        style={{ backgroundColor: imageTextColor }}
                      >
                        <input
                          type="color"
                          value={imageTextColor}
                          onChange={(e) => setImageTextColor(e.target.value)}
                          className="w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                      <Input
                        value={imageTextColor}
                        onChange={(e) => setImageTextColor(e.target.value)}
                        className="flex-1 bg-gray-900 border-gray-600 text-white text-xs"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Font Size</Label>
                    <Select value={imageFontSize} onValueChange={setImageFontSize}>
                      <SelectTrigger className="bg-gray-900 border-gray-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600">
                        <SelectItem value="12">12px</SelectItem>
                        <SelectItem value="14">14px</SelectItem>
                        <SelectItem value="16">16px</SelectItem>
                        <SelectItem value="18">18px</SelectItem>
                        <SelectItem value="20">20px</SelectItem>
                        <SelectItem value="24">24px</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button 
                  onClick={convertTextToImage} 
                  className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
                  data-testid="convert-text-to-image-btn"
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Convert to Image
                </Button>
              </CardContent>
            </Card>

            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <ImageIcon className="w-5 h-5 text-purple-400" />
                  Generated Image
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gray-900 rounded-lg p-4 min-h-[200px] flex items-center justify-center border border-gray-700">
                  {imageDataUrl ? (
                    <img src={imageDataUrl} alt="Generated" className="max-w-full h-auto rounded" data-testid="generated-image" />
                  ) : (
                    <div className="text-center text-gray-500">
                      <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                      <p>Image preview will appear here</p>
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={() => downloadFile(imageDataUrl, 'text-image.png')} 
                  variant="outline" 
                  className="w-full border-gray-600 text-white hover:bg-gray-700"
                  disabled={!imageDataUrl}
                  data-testid="download-generated-image-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Image
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Steganography */}
        <TabsContent value="steganography" className="space-y-6">
          <Alert className="bg-purple-900/30 border-purple-600/50">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <AlertDescription className="text-purple-200">
              <strong>Steganography:</strong> Hide secret text inside images. The text is encoded in the least significant bits of pixels, making it invisible to the naked eye.
            </AlertDescription>
          </Alert>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Hide Text in Image */}
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <EyeOff className="w-5 h-5 text-cyan-400" />
                  Hide Text in Image
                </CardTitle>
                <CardDescription className="text-gray-400">Embed secret text inside an image</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Carrier Image</Label>
                  <div 
                    onClick={() => carrierInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-600 rounded-xl p-4 text-center cursor-pointer hover:border-cyan-400 transition-colors"
                  >
                    {carrierImage ? (
                      <img src={carrierImage} alt="Carrier" className="max-h-32 mx-auto rounded" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-400 text-sm">Click to select carrier image</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={carrierInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCarrierImageSelect}
                    className="hidden"
                    data-testid="carrier-image-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Secret Text</Label>
                  <Textarea
                    placeholder="Enter secret text to hide..."
                    value={hideText}
                    onChange={(e) => setHideText(e.target.value)}
                    className="bg-gray-900 border-gray-600 text-white"
                    data-testid="secret-text-input"
                  />
                </div>
                
                <Button 
                  onClick={hideTextInImage} 
                  className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500"
                  disabled={!carrierImage || !hideText}
                  data-testid="hide-text-btn"
                >
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide Text in Image
                </Button>
                
                {stegoImage && (
                  <div className="space-y-2">
                    <Label className="text-white">Result (with hidden text)</Label>
                    <img src={stegoImage} alt="Stego" className="max-w-full rounded border border-gray-600" data-testid="stego-image" />
                    <Button 
                      onClick={() => downloadFile(stegoImage, 'hidden-message.png')} 
                      variant="outline" 
                      className="w-full border-gray-600 text-white hover:bg-gray-700"
                      data-testid="download-stego-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Image with Hidden Text
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Extract Text from Image */}
            <Card className="border-gray-700 bg-gray-800/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Eye className="w-5 h-5 text-purple-400" />
                  Extract Hidden Text
                </CardTitle>
                <CardDescription className="text-gray-400">Reveal secret text from an image</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Image with Hidden Text</Label>
                  <div 
                    onClick={() => extractInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-purple-400 transition-colors"
                  >
                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-white font-medium">Click to select image</p>
                    <p className="text-gray-400 text-sm mt-1">Upload an image with hidden text</p>
                  </div>
                  <input
                    ref={extractInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleExtractImage}
                    className="hidden"
                    data-testid="extract-image-input"
                  />
                </div>
                
                {extractedText && (
                  <div className="space-y-2">
                    <Label className="text-white">Extracted Secret Text</Label>
                    <div className="bg-gray-900 rounded-lg p-4 border border-gray-600">
                      <pre className="text-cyan-400 whitespace-pre-wrap break-all" data-testid="extracted-text">{extractedText}</pre>
                    </div>
                    <Button 
                      onClick={() => copyToClipboard(extractedText)} 
                      variant="outline" 
                      className="w-full border-gray-600 text-white hover:bg-gray-700"
                      data-testid="copy-extracted-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Extracted Text
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FileEncoder;